import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch, useSelector } from "react-redux";
import { fetchSessionsV2 } from "../../redux/actions/sessionActions";
import { fetchOrganizations } from "../../redux/actions/organizationActions";
import { RootState, AppDispatch } from "../../redux/store";
import { format } from "date-fns";
import { debounce } from "lodash";
import DetailPanel from "./schedule/DetailPanel";
import { SessionFilters } from "./sessions";
import {
  SessionFilters as SessionFiltersType,
  EnrichedSession,
  parseFiltersFromUrl,
  filtersToUrlParams,
  OrganizationOption,
  ExperienceOption,
} from "./sessions/types";
import { buildResLinkUrl } from "./sessions/utils";
import { api } from "../../api_middleware";

// Status color mapping
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  creating: { bg: "#e0e0e0", text: "#424242" },
  ready_to_configure: { bg: "#fff3e0", text: "#e65100" },
  configuring: { bg: "#fff3e0", text: "#e65100" },
  ready_to_start: { bg: "#e3f2fd", text: "#1565c0" },
  in_progress: { bg: "#e8f5e9", text: "#2e7d32" },
  finished: { bg: "#f3e5f5", text: "#7b1fa2" },
  finalized: { bg: "#ede7f6", text: "#512da8" },
};

const SessionsTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    sessions,
    enrichedSessions,
    loading,
    loadingMore,
    error,
    page_number,
    totalCount,
  } = useSelector((state: RootState) => state.sessions);

  // Get organizations from Redux
  const { organizations: reduxOrganizations, loading: orgsLoading } =
    useSelector((state: RootState) => state.organizations);

  const [selectedSession, setSelectedSession] =
    useState<EnrichedSession | null>(null);
  const [localFilters, setLocalFilters] = useState<SessionFiltersType>(() =>
    parseFiltersFromUrl(searchParams)
  );

  // Filter dropdown options
  const [assessments, setAssessments] = useState<ExperienceOption[]>([]);
  const [roleplays, setRoleplays] = useState<ExperienceOption[]>([]);
  const [loadingExperiences, setLoadingExperiences] = useState(false);

  // State for copy link feedback
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  // Convert Redux organizations to OrganizationOption format
  const organizations: OrganizationOption[] = useMemo(() => {
    return reduxOrganizations.map((org) => ({
      id: org.id,
      name: org.name || org.id,
    }));
  }, [reduxOrganizations]);

  // Create a lookup map for organization names
  const orgNameLookup: Record<string, string> = useMemo(() => {
    const lookup: Record<string, string> = {};
    reduxOrganizations.forEach((org) => {
      lookup[org.id] = org.name || org.id;
    });
    return lookup;
  }, [reduxOrganizations]);

  const PAGE_SIZE = 25;

  // Load organizations from Redux
  useEffect(() => {
    if (reduxOrganizations.length === 0) {
      dispatch(fetchOrganizations());
    }
  }, [dispatch, reduxOrganizations.length]);

  // Load assessments and roleplays
  useEffect(() => {
    const loadExperiences = async () => {
      setLoadingExperiences(true);
      try {
        // Load assessments
        const assessmentsRes = await api("v1/admin/assessments", "GET");
        if (assessmentsRes) {
          setAssessments(
            assessmentsRes.map((a: any) => ({
              id: a.id,
              resourceKey: a.resource_key,
              name: a.name || a.resource_key,
              organizationId: a.organization_id,
              experienceType: "assessment" as const,
            }))
          );
        }

        // Load roleplays
        const roleplaysRes = await api("v1/admin/roleplays", "GET");
        if (roleplaysRes) {
          setRoleplays(
            roleplaysRes.map((r: any) => ({
              id: r.id,
              resourceKey: r.resource_key,
              name: r.name || r.resource_key,
              organizationId: r.organization_id,
              experienceType: "roleplay" as const,
            }))
          );
        }
      } catch (err) {
        console.error("Failed to load experiences:", err);
      } finally {
        setLoadingExperiences(false);
      }
    };

    loadExperiences();
  }, []);

  // Helper function to format time ago
  const formatTimeAgo = useCallback((createdAt: string): string => {
    const now = Date.now();
    const created = new Date(createdAt).getTime();
    const diffMs = now - created;
    const seconds = Math.floor(diffMs / 1000);

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `-${hours}:${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}s`;
    } else if (minutes > 0) {
      return `-${minutes}:${secs.toString().padStart(2, "0")}s`;
    } else {
      return `-${secs}s`;
    }
  }, []);

  // Helper function to calculate session duration from state_history
  const getSessionDuration = useCallback(
    (
      row: EnrichedSession
    ): { durationSec: number | null; isOngoing: boolean } => {
      const stateHistory = row.api_session.state_history;
      if (!stateHistory || stateHistory.length === 0) {
        return { durationSec: null, isOngoing: false };
      }

      // Find when session entered "in_progress" state
      const inProgressState = stateHistory.find(
        (s: any) => s.data?.status === "in_progress"
      );
      if (!inProgressState?.entered_at) {
        return { durationSec: null, isOngoing: false };
      }

      const startTime = new Date(inProgressState.entered_at).getTime();

      // Find when session finished (finished or finalized)
      const finishedState = stateHistory.find(
        (s: any) =>
          s.data?.status === "finished" || s.data?.status === "finalized"
      );

      let endTime: number;
      let isOngoing = false;

      if (finishedState?.entered_at) {
        endTime = new Date(finishedState.entered_at).getTime();
      } else {
        // Session is still ongoing
        endTime = Date.now();
        isOngoing = true;
      }

      const durationSec = (endTime - startTime) / 1000;
      return { durationSec, isOngoing };
    },
    []
  );

  // Format duration in human-readable format
  const formatDuration = useCallback((seconds: number): string => {
    if (seconds < 60) {
      return `${Math.floor(seconds)}s`;
    } else if (seconds < 3600) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}m ${secs}s`;
    } else {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return `${hrs}h ${mins}m`;
    }
  }, []);

  const fetchSessionsWithParams = useCallback(
    (
      params: {
        page_number?: number;
        reset?: boolean;
        filters?: SessionFiltersType;
      } = {}
    ) => {
      const effectiveFilters = params.filters ?? localFilters;
      dispatch(
        fetchSessionsV2({
          limit: PAGE_SIZE,
          filters: effectiveFilters,
          ...params,
        })
      );
    },
    [dispatch, localFilters]
  );

  const handleRefresh = useCallback(() => {
    fetchSessionsWithParams({ reset: true, page_number: 0 });
  }, [fetchSessionsWithParams]);

  const debouncedFetchSessions = useMemo(
    () =>
      debounce((filters: SessionFiltersType) => {
        fetchSessionsWithParams({ reset: true, filters });
      }, 300),
    [fetchSessionsWithParams]
  );

  // Sync URL params with local filters
  useEffect(() => {
    const urlFilters = parseFiltersFromUrl(searchParams);
    setLocalFilters(urlFilters);
  }, [searchParams]);

  // Initial fetch
  useEffect(() => {
    debouncedFetchSessions(localFilters);
    return () => {
      debouncedFetchSessions.cancel();
    };
  }, [debouncedFetchSessions, localFilters]);

  const handleFiltersChange = useCallback(
    (newFilters: SessionFiltersType) => {
      setLocalFilters(newFilters);
      // Update URL
      const params = filtersToUrlParams(newFilters);
      setSearchParams(params);
      // Fetch with new filters
      debouncedFetchSessions(newFilters);
    },
    [setSearchParams, debouncedFetchSessions]
  );

  const handlePaginationChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      if (model.page !== page_number && !loadingMore) {
        fetchSessionsWithParams({
          page_number: model.page,
        });
      }
    },
    [page_number, loadingMore, fetchSessionsWithParams]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "session_id",
        headerName: "Session",
        width: 90,
        sortable: false,
        headerAlign: "center",
        align: "center",
        valueGetter: (value, row) => row.api_session.session_id,
        renderCell: (params) => (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setSelectedSession(params.row);
            }}
            sx={{
              cursor: "pointer",
              fontFamily: "monospace",
              fontSize: "0.75rem",
              color: "#1976d2",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {params.value?.substring(0, 6)}...
          </Box>
        ),
      },
      {
        field: "user_email",
        headerName: "User",
        width: 120,
        sortable: false,
        headerAlign: "center",
        valueGetter: (value, row) => row.api_session.user_email,
        renderCell: (params) => {
          if (!params.value) return "-";
          const internalUserId = params.row.api_session.internal_user_id;
          if (!internalUserId) return params.value;
          return (
            <Box
              component="a"
              href={`/dashboard/users/${internalUserId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={params.value}
              sx={{
                cursor: "pointer",
                color: "#1976d2",
                textDecoration: "none",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
                display: "block",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {params.value}
            </Box>
          );
        },
      },
      {
        field: "conversation_id",
        headerName: "Chat",
        width: 100,
        sortable: false,
        headerAlign: "center",
        align: "center",
        valueGetter: (value, row) => row.api_session.conversation_id,
        renderCell: (params) => {
          const row = params.row as EnrichedSession;
          if (!params.value) return "-";
          // Link to chat session within conversation if chat_session_id available
          const chatSessionId = row.chat_session_id;
          const linkPath = chatSessionId
            ? `/dashboard/conversations/${params.value}?target_id=msg:${chatSessionId}:0`
            : `/dashboard/conversations/${params.value}`;
          return (
            <Chip
              label="View Chat"
              size="small"
              color="primary"
              variant="outlined"
              component="a"
              href={linkPath}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{
                cursor: "pointer",
                fontSize: "0.65rem",
                height: 24,
              }}
            />
          );
        },
      },
      {
        field: "experience",
        headerName: "Experience",
        flex: 1,
        minWidth: 150,
        sortable: false,
        headerAlign: "center",
        renderCell: (params) => {
          const row = params.row as EnrichedSession;
          const experienceInfo = row.experience_info;
          if (!experienceInfo?.experience_type) return "-";

          const isAssessment = experienceInfo.experience_type === "assessment";
          // Link to assessments/roleplays tab with key prefilled as search and org filter
          const experienceKey = experienceInfo.experience_key || "";
          const orgId = row.api_session.organization_id || "";
          const urlParams = new URLSearchParams();
          if (experienceKey) urlParams.set("search", experienceKey);
          if (orgId) urlParams.set("org", orgId);
          const linkPath = isAssessment
            ? `/dashboard/assessments?${urlParams.toString()}`
            : `/dashboard/roleplays?${urlParams.toString()}`;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Chip
                label={isAssessment ? "A" : "R"}
                size="small"
                color={isAssessment ? "primary" : "secondary"}
                sx={{ width: 26, height: 22, fontSize: "0.7rem" }}
              />
              <Box
                component="a"
                href={linkPath}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  cursor: "pointer",
                  color: "#1976d2",
                  textDecoration: "none",
                  fontSize: "0.8rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 120,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                title={
                  experienceInfo.experience_name ||
                  experienceInfo.experience_key
                }
              >
                {experienceInfo.experience_name ||
                  experienceInfo.experience_key ||
                  "-"}
              </Box>
            </Box>
          );
        },
      },
      {
        field: "artifact",
        headerName: "Artifact",
        width: 80,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const row = params.row as EnrichedSession;
          const experienceInfo = row.experience_info;
          if (!experienceInfo?.has_artifact || !experienceInfo.artifact_id)
            return "-";

          const isAssessment = experienceInfo.experience_type === "assessment";
          const orgId = row.api_session.organization_id;
          const linkPath = isAssessment
            ? `/dashboard/assessments/${experienceInfo.experience_id}/sessions/${experienceInfo.artifact_id}?org=${orgId}`
            : `/dashboard/roleplays/${orgId}/${experienceInfo.experience_id}/sessions/${experienceInfo.artifact_id}?org=${orgId}`;

          return (
            <Chip
              label="View"
              size="small"
              color="success"
              variant="outlined"
              component="a"
              href={linkPath}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{
                cursor: "pointer",
                fontSize: "0.7rem",
                height: 24,
              }}
            />
          );
        },
      },
      {
        field: "launch_link",
        headerName: "URL",
        width: 50,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const row = params.row as EnrichedSession;
          if (!row.launch_args_id) return null;
          const url = buildResLinkUrl(
            row.launch_args_id,
            row.api_session.organization_id,
            row.api_session.connection_method
          );
          const isCopied = copiedLinkId === row.api_session.session_id;
          return (
            <Tooltip title={url} placement="left">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(url);
                  setCopiedLinkId(row.api_session.session_id);
                  setTimeout(() => setCopiedLinkId(null), 2000);
                }}
                sx={{ padding: 0.5 }}
              >
                {isCopied ? (
                  <CheckCircleIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                ) : (
                  <LinkIcon sx={{ fontSize: 16, color: "#1976d2" }} />
                )}
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: "organization_id",
        headerName: "Org",
        width: 110,
        sortable: false,
        headerAlign: "center",
        align: "center",
        valueGetter: (value, row) => row.api_session.organization_id,
        renderCell: (params) => {
          if (!params.value) return "-";
          const orgName =
            orgNameLookup[params.value] || params.value.substring(0, 6) + "...";
          return (
            <Box
              component="a"
              href={`/dashboard/organizations/${params.value}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{
                cursor: "pointer",
                fontSize: "0.8rem",
                color: "#1976d2",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
              title={orgNameLookup[params.value] || params.value}
            >
              {orgName.length > 12 ? orgName.substring(0, 12) + "..." : orgName}
            </Box>
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created",
        width: 105,
        sortable: false,
        headerAlign: "center",
        align: "center",
        valueGetter: (value, row) => row.api_session.created_at,
        renderCell: (params) => {
          if (!params.value) return "-";
          const formatted = format(new Date(params.value), "MM-dd HH:mm");
          return (
            <Box
              sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
              title={format(new Date(params.value), "yyyy-MM-dd HH:mm:ss")}
            >
              {formatted}
            </Box>
          );
        },
      },
      {
        field: "codec_info",
        headerName: "Codec",
        width: 90,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const codec =
            params.row.api_session.telemetry?.negotiated_codec?.codec;
          const durationSec =
            params.row.api_session.telemetry?.negotiated_codec
              ?.negotiation_duration_sec;
          if (codec) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                }}
              >
                <span>{codec}</span>
                {durationSec !== undefined && (
                  <span style={{ color: "#666", fontSize: "0.7rem" }}>
                    {durationSec.toFixed(1)}s
                  </span>
                )}
              </Box>
            );
          }
          return "-";
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 90,
        sortable: false,
        headerAlign: "center",
        align: "center",
        valueGetter: (value, row) => row.api_session.status,
        renderCell: (params) => {
          const apiSession = params.row.api_session;
          let status = apiSession.status;
          if (apiSession.state_history && apiSession.state_history.length > 0) {
            const mostRecent =
              apiSession.state_history[apiSession.state_history.length - 1];
            status = mostRecent.data?.status || apiSession.status;
          }
          if (!status) return "-";

          const colors = STATUS_COLORS[status] || {
            bg: "#e0e0e0",
            text: "#424242",
          };
          return (
            <Chip
              label={status.replace(/_/g, " ")}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.65rem",
                fontWeight: 500,
                bgcolor: colors.bg,
                color: colors.text,
                textTransform: "capitalize",
              }}
            />
          );
        },
      },
      {
        field: "session_duration",
        headerName: "Duration",
        width: 90,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const { durationSec, isOngoing } = getSessionDuration(params.row);
          if (durationSec === null) return "-";
          return (
            <Box
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                color: isOngoing ? "#2e7d32" : "inherit",
              }}
              title={
                isOngoing
                  ? "Session ongoing"
                  : `Session duration: ${durationSec.toFixed(0)}s`
              }
            >
              {formatDuration(durationSec)}
              {isOngoing && " ⏱"}
            </Box>
          );
        },
      },
      {
        field: "ago",
        headerName: "Ago",
        width: 90,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Box sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
            {formatTimeAgo(params.row.api_session.created_at)}
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 40,
        sortable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/dashboard/sessions/${params.row.api_session.session_id}`
              );
            }}
            sx={{ padding: 0.5 }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ),
      },
    ],
    [
      formatTimeAgo,
      formatDuration,
      getSessionDuration,
      navigate,
      orgNameLookup,
      copiedLinkId,
    ]
  );

  // Use enriched sessions if available, fall back to regular sessions
  const displaySessions =
    enrichedSessions.length > 0 ? enrichedSessions : sessions;

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h5">Pixel Streaming Sessions</Typography>
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={loading}
            sx={{ color: "#1976d2" }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <SessionFilters
        filters={localFilters}
        onFiltersChange={handleFiltersChange}
        organizations={organizations}
        assessments={assessments}
        roleplays={roleplays}
        loading={loading || orgsLoading}
        loadingExperiences={loadingExperiences}
      />

      <Box
        sx={{
          flex: 1,
          position: "relative",
          minHeight: "60vh",
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <DataGrid
          rows={displaySessions}
          columns={columns}
          getRowId={(row) => row.api_session.session_id}
          disableRowSelectionOnClick
          paginationMode="server"
          paginationModel={{ page: page_number, pageSize: PAGE_SIZE }}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={[PAGE_SIZE]}
          rowCount={totalCount ?? -1}
          loading={loadingMore}
          rowHeight={48}
          sx={{
            "& .MuiDataGrid-cell": {
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-columnHeader": {
              padding: "8px 12px",
            },
            "& .MuiDataGrid-row": {
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              },
            },
          }}
        />

        {/* Detail Panel - Sliding Overlay */}
        {selectedSession && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              width: { xs: "100%", sm: "500px", md: "600px" },
              backgroundColor: "#ffffff",
              boxShadow: "-2px 0 8px rgba(0, 0, 0, 0.15)",
              zIndex: 1300,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              animation: "slideIn 0.3s ease-out",
              "@keyframes slideIn": {
                from: {
                  transform: "translateX(100%)",
                },
                to: {
                  transform: "translateX(0)",
                },
              },
            }}
          >
            <DetailPanel
              node={null}
              session={null}
              unscheduledSession={
                selectedSession
                  ? {
                      s: {
                        ...selectedSession.api_session,
                        launch_args_id: selectedSession.launch_args_id,
                      } as any,
                      rationale_per_node: {},
                    }
                  : null
              }
              nearlySchedulableSession={null}
              onCloseNode={() => {}}
              onCloseSession={() => {}}
              onCloseUnscheduledSession={() => setSelectedSession(null)}
              onCloseNearlySchedulableSession={() => {}}
            />
          </Box>
        )}

        {/* Backdrop when panel is open */}
        {selectedSession && (
          <Box
            onClick={() => setSelectedSession(null)}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: 1299,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default SessionsTab;
